export type StreamSettleOptions = {
  /** Never resolve before this — gives late config emits time to apply off-screen. */
  minMs?: number;
  /** Required continuous hitch-free playback window before resolving. */
  stableMs?: number;
  /** A gap between presented frames above this counts as a hitch and restarts the window. */
  maxGapMs?: number;
  /** Absolute cap so the gate can never trap the user behind the loader. */
  maxMs?: number;
  /** Flat wait used when requestVideoFrameCallback is unavailable. */
  fallbackMs?: number;
};

export type StreamSettleGate = {
  /** Resolves true once settled (or capped), false when cancelled. */
  promise: Promise<boolean>;
  cancel: () => void;
};

/**
 * Waits until the pixel-stream <video> has delivered frames smoothly for a
 * continuous window — i.e. Unreal is done with the visible model build-up
 * (texture/LOD streaming, shader hitches) — so the caller can reveal the
 * stream without the user watching the boat assemble.
 */
export function waitForStreamSettle(
  video: HTMLVideoElement | null | undefined,
  {
    minMs = 1800,
    stableMs = 1200,
    maxGapMs = 250,
    maxMs = 8000,
    fallbackMs = 3000,
  }: StreamSettleOptions = {},
): StreamSettleGate {
  let done = false;
  let timeoutId: number | null = null;
  let vfcId: number | null = null;

  let settle!: (settled: boolean) => void;
  const promise = new Promise<boolean>((resolve) => {
    settle = resolve;
  });

  const finish = (settled: boolean) => {
    if (done) return;
    done = true;
    if (timeoutId != null) window.clearTimeout(timeoutId);
    if (vfcId != null) (video as any)?.cancelVideoFrameCallback?.(vfcId);
    settle(settled);
  };

  const rvfc = (video as any)?.requestVideoFrameCallback?.bind(video);

  if (!rvfc) {
    timeoutId = window.setTimeout(
      () => finish(true),
      Math.max(minMs, fallbackMs),
    );
  } else {
    const start = performance.now();
    let stableStart = start;
    let lastFrame = start;

    const tick = (now: number) => {
      if (done) return;
      if (now - lastFrame > maxGapMs) stableStart = now;
      lastFrame = now;

      if (now - start >= minMs && now - stableStart >= stableMs) {
        finish(true);
        return;
      }
      vfcId = rvfc(tick);
    };

    vfcId = rvfc(tick);
    // Cap; also the backstop in case frames stop arriving entirely.
    timeoutId = window.setTimeout(() => finish(true), maxMs);
  }

  return { promise, cancel: () => finish(false) };
}
